import React, { useMemo, useState, useEffect, useCallback } from 'react';
import FiltrosPanel, { CAMPO } from '../../components/FiltrosPanel';
import PaginacaoFooter from '../../components/PaginacaoFooter';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    Box,
    Stack,
    Button,
    Typography,
    Autocomplete,
    TextField,
    Grid,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    MenuItem,
    Divider,
    useTheme,
    useMediaQuery,
    Skeleton,
    LinearProgress,
    FormControlLabel,
    Switch,
    InputAdornment,
    Chip,
    FormControl,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Card,
    CardHeader,
    CardContent,
    Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
} from '@mui/lab';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import * as Lucide from 'lucide-react';

import { useExperimentGroups } from './useExperimentGroups';
import useSessionState from '../../hooks/useSessionState';
import ExperimentCard from '../../components/ExperimentCard';
import ProcessDialog from './ProcessDialog';
import GrowthCurveModal from './GrowthCurveModal';
import { useBootstrap } from '../../context/BootstrapContext';
import { usePermissao } from '../../security/usePermissao';
import { searchTowers } from '../../infra/api/Torres';

import { GraficoTipo } from '../../infra/api/GraficoApi';
import { useCharts } from '../../hooks/useCharts';
import ColorTimelineChart from '../../components/ColorTimelineChart';
import MultiSeriesChart from '../../components/MultiSeriesChart';
import { detectSensorType } from './utils/sensorDetection';

dayjs.locale('pt-br');

const SORT_OPTIONS = [
    { value: 'startDate', label: 'Início' },
    { value: 'endDate', label: 'Fim' },
    { value: 'status', label: 'Status' },
    { value: 'serialNumber', label: 'Número de série' },
    { value: 'protocolId', label: 'Protocolo (ID)' },
    { value: 'id', label: 'Experimento (ID)' },
];

const initialFilters = {
    experimentId: '',
    serialNumber: '',
    protocolId: '',
    analysisId: '',
    temLabResult: '',
    firmwareVersion: '',
    startFromDate: null,
    startToDate: null,
    sort: 'startDate',
    dir: 'desc',
};

const hydrateFilters = (savedState) => {
    if (!savedState) return initialFilters;
    return {
        // initialFilters primeiro: sessoes salvas ANTES de um filtro novo existir nao tem
        // a chave, e sem esse default o campo viria undefined.
        ...initialFilters,
        ...savedState,
        startFromDate: savedState.startFromDate
            ? dayjs(savedState.startFromDate)
            : null,
        startToDate: savedState.startToDate
            ? dayjs(savedState.startToDate)
            : null,
    };
};


function formatLabel(dateString) {
    const d = dayjs(dateString).startOf('day');
    const today = dayjs().startOf('day');
    const diff = today.diff(d, 'day');
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Ontem';
    if (diff < 7) return d.format('dddd').replace(/^./, (c) => c.toUpperCase());
    return d.format('DD/MM/YYYY');
}

const BASIC_TABS = [
    { key: GraficoTipo.SPECTRAL_TURBIDIMETRY, label: 'Turbidimetria' },
    { key: GraficoTipo.SPECTRAL_FLUORESCENCE, label: 'Fluorescência' },
];

function BasicChartModalContent({ experiment }) {
    const { charts, errors, load } = useCharts(experiment.id);
    const [activeTab, setActiveTab] = useState(GraficoTipo.SPECTRAL_TURBIDIMETRY);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        load(GraficoTipo.SPECTRAL_TURBIDIMETRY);
        load(GraficoTipo.SPECTRAL_FLUORESCENCE);
    }, [load]);

    const rawChartData = charts[activeTab];
    const isLoading = !rawChartData && !errors[activeTab];

    const sensorType = useMemo(
        () => detectSensorType(activeTab, experiment, rawChartData),
        [activeTab, experiment, rawChartData]
    );

    const chartData = useMemo(() => {
        if (!rawChartData || !rawChartData.series) return rawChartData;
        const filteredSeries = {};
        Object.entries(rawChartData.series).forEach(([group, seriesList]) => {
            filteredSeries[group] = seriesList.filter((s) =>
                s.name.toUpperCase().endsWith('.F2') ||
                s.name.toUpperCase() === 'F2'
            );
        });
        return { ...rawChartData, series: filteredSeries };
    }, [rawChartData]);

    const hasData = chartData && chartData.labels && chartData.labels.length > 0;
    const hasColorTimeline = (rawChartData?.meta?.colorTimeline?.length ?? 0) >= 2;
    const activeLabel = BASIC_TABS.find((t) => t.key === activeTab)?.label ?? '';

    return (
        <DialogContent
            dividers
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1, sm: 1.5 },
                overflow: 'hidden',
                gap: 1,
            }}
        >
            {/* Seletor de tipo de gráfico */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}
            >
                {BASIC_TABS.map((tab) => (
                    <Tab key={tab.key} label={tab.label} value={tab.key} />
                ))}
            </Tabs>

            {isLoading ? (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress />
                    <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                        Carregando dados do sensor...
                    </Typography>
                </Box>
            ) : !hasData ? (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                    }}
                >
                    <Lucide.ChartSpline
                        size={40}
                        color={theme.palette.text.disabled}
                        style={{ marginBottom: 8 }}
                    />
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        Aguardando dados do sensor
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Cor x Tempo — topo, compacto */}
                    {hasColorTimeline && (
                        <Box sx={{ flexShrink: 0 }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    display: 'block',
                                    textAlign: 'center',
                                    lineHeight: 1.4,
                                    color: 'text.secondary',
                                    mb: 0.5,
                                }}
                            >
                                Cor × Tempo
                            </Typography>
                            <ColorTimelineChart
                                chart={rawChartData}
                                height={isMobile ? 25 : 35}
                            />
                        </Box>
                    )}

                    {/* Gráfico principal — cresce para preencher o espaço restante */}
                    <Card
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 3,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        }}
                    >
                        <CardHeader
                            title={
                                <Typography
                                    variant="subtitle1"
                                    component="h3"
                                    fontWeight={600}
                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                >
                                    {activeLabel}
                                </Typography>
                            }
                            sx={{ py: 1, px: 2, flexShrink: 0 }}
                        />
                        <CardContent
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                p: '0 12px 12px !important',
                            }}
                        >
                            {errors[activeTab] ? (
                                <Stack
                                    sx={{
                                        flex: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Alert severity="error" sx={{ width: '100%' }}>
                                        {errors[activeTab]}
                                    </Alert>
                                </Stack>
                            ) : (
                                <MultiSeriesChart
                                    data={chartData}
                                    sensorType={sensorType}
                                    graficoTipo={activeTab}
                                />
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </DialogContent>
    );
}

function BasicChartModal({ experiment, onClose }) {
    const navigate = useNavigate();
    const { tenant } = useParams();

    return (
        <Dialog
            open={!!experiment}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
            <DialogTitle sx={{ flexShrink: 0 }}>Colorimetria</DialogTitle>
            {experiment && (
                <BasicChartModalContent
                    experiment={experiment}
                    key={experiment.id}
                />
            )}
            <DialogActions
                sx={{ justifyContent: 'space-between', px: 2, py: 1.5, flexShrink: 0 }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<Lucide.Lock />}
                    onClick={() => { onClose(); navigate(`/${tenant}/planos`); }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                    Desbloquear análise completa
                </Button>
                <Button onClick={onClose} variant="contained" disableElevation>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const DayGroupContent = ({
    date,
    protos,
    handleAction,
    canViewGraphs,
    viewedExperiments,
    lastViewedExperimentId,
    isMobile,
    towersData,
    loadingTowers,
    protocols,
    selectable,
    selectedIds,
    onToggleSelect,
}) => {
    const theme = useTheme();
    return (
    <Paper
        elevation={0}
        variant="outlined"
        sx={{
            p: { xs: 1.5, sm: 3 },
            borderRadius: 3,
            width: '100%',
            overflow: 'hidden',
            background: (theme) =>
                theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.72)'
                    : 'rgba(18, 24, 40, 0.65)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderColor: (theme) =>
                theme.palette.mode === 'light'
                    ? 'rgba(255, 255, 255, 0.70)'
                    : 'rgba(255, 255, 255, 0.08)',
            boxShadow: (theme) =>
                theme.palette.mode === 'light'
                    ? '0 4px 24px rgba(14, 34, 59, 0.10), 0 1px 4px rgba(14, 34, 59, 0.05)'
                    : '0 4px 24px rgba(0, 0, 0, 0.45)',
        }}
    >
        {isMobile ? (
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <Lucide.CalendarDays color={theme.palette.primary.main} size={22} />
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    {formatLabel(date)}
                </Typography>
            </Stack>
        ) : (
            <Typography
                variant="h6"
                fontWeight="bold"
                color="text.primary"
                gutterBottom
            >
                {formatLabel(date)}
            </Typography>
        )}

        {protos.map(({ protoName, protoColor, analyses }) => (
            <Box
                component="div"
                key={protoName}
                sx={{
                    mt: 2,
                    '&:not(:first-of-type)': { mt: 3 },
                    border: '1px dashed',
                    borderColor: (theme) =>
                        alpha(protoColor || theme.palette.primary.main, 0.3),
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: protoColor || 'primary.main',
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: protoColor || 'text.secondary' }}
                    >
                        {protoName}
                    </Typography>
                </Box>

                {analyses.map(({ analysisName, items }, index) => {
                    const isLastGroup = index === analyses.length - 1;

                    return (
                        <Grid
                            container
                            spacing={{ xs: 2, md: 3 }}
                            key={analysisName}
                            sx={{ mb: isLastGroup ? 0 : 3 }}
                        >
                            {items.map((exp) => {
                                const isLast =
                                    lastViewedExperimentId === String(exp.id);
                                const isViewed = viewedExperiments.has(
                                    String(exp.id)
                                );
                                const towerInfo = exp.serialNumber
                                    ? towersData[exp.serialNumber]
                                    : null;
                                const towerLoading = exp.serialNumber
                                    ? loadingTowers.has(exp.serialNumber)
                                    : false;

                                return (
                                    <Grid
                                        item
                                        key={exp.id}
                                        xs={12}
                                        sm={12}
                                        md={6}
                                        // OTIMIZAÇÃO DE BREAKPOINTS:
                                        // Em lg (Laptops 13-15"), usamos 6 colunas (50%) = 2 cartões
                                        // Em xl (Telas > 1536px), usamos 4 colunas (33%) = 3 cartões
                                        lg={6}
                                        xl={4}
                                    >
                                        <ExperimentCard
                                            experiment={{
                                                ...exp,
                                                generalInfo: {
                                                    status: exp.status,
                                                    startDate: exp.startDate,
                                                    endDate: exp.endDate,
                                                    protocolId: exp.protocolId,
                                                },
                                            }}
                                            protocols={protocols}
                                            towerTag={exp.nome || towerInfo?.tag}
                                            towerTagColor={exp.tagColor || towerInfo?.tagColor}
                                            towerTagLoading={towerLoading}
                                            onAction={handleAction}
                                            canNavigate={canViewGraphs}
                                            isViewed={isViewed}
                                            isLastViewed={isLast}
                                            selectable={selectable}
                                            selected={selectedIds?.has(String(exp.id))}
                                            onToggleSelect={onToggleSelect}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    );
                })}
            </Box>
        ))}
    </Paper>
    );
};

export default function ExperimentsPage({ onBack }) {
    const navigate = useNavigate();
    const { tenant } = useParams();
    const { bootstrap } = useBootstrap();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { pode } = usePermissao();

    const [status, setStatus] = useSessionState(
        'experimentsPage_status',
        'active'
    );
    const [page, setPage] = useSessionState('experimentsPage_page', 0);
    const [size, setSize] = useSessionState('experimentsPage_size', 20);
    const [showDeleted, setShowDeleted] = useSessionState(
        'experimentsPage_showDeleted',
        false
    );
    const [filters, setFilters] = useSessionState(
        'experimentsPage_filters',
        initialFilters,
        hydrateFilters
    );
    const [committedFilters, setCommittedFilters] = useSessionState(
        'experimentsPage_committedFilters',
        initialFilters,
        hydrateFilters
    );

    const [viewedExperiments, setViewedExperiments] = useSessionState(
        'viewedExperiments',
        new Set(),
        (saved) => {
            if (Array.isArray(saved)) {
                return new Set(saved.map(String));
            }
            return new Set();
        }
    );

    const [lastViewedExperimentId, setLastViewedExperimentId] = useSessionState(
        'lastViewedExperimentId',
        null,
        (saved) => (saved ? String(saved) : null)
    );

    const [processOpen, setProcessOpen] = useState(false);
    const [processTarget, setProcessTarget] = useState(null);
    const [towersData, setTowersData] = useState({});
    const [loadingTowers, setLoadingTowers] = useState(new Set());
    const [basicModalExp, setBasicModalExp] = useState(null);

    // Seleção de experimentos (réplicas) para a curva de crescimento consolidada.
    const [selectionMode, setSelectionMode] = useState(false);
    const [growthModalOpen, setGrowthModalOpen] = useState(false);
    const [selectedMap, setSelectedMap] = useSessionState(
        'growthSelectedExps',
        {},
        (saved) =>
            saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {}
    );
    const selectedIds = useMemo(() => new Set(Object.keys(selectedMap)), [selectedMap]);
    const selectedExperiments = useMemo(() => Object.values(selectedMap), [selectedMap]);
    const selectionMixed = useMemo(() => {
        const keys = new Set(
            selectedExperiments.map(
                (e) => `${e.protocolName || ''}|${e.analysisName || ''}`
            )
        );
        return keys.size > 1;
    }, [selectedExperiments]);

    const handleToggleSelect = useCallback(
        (exp) => {
            const id = String(exp.id);
            setSelectedMap((prev) => {
                const next = { ...prev };
                if (next[id]) {
                    delete next[id];
                } else {
                    next[id] = {
                        id,
                        nome: exp.nome,
                        serialNumber: exp.serialNumber,
                        tagColor: exp.tagColor,
                        protocolName: exp.protocolName,
                        analysisName: exp.analysisName,
                        // spectral: ganho/tempo de integração p/ o Basic Count no modal
                        spectral: exp.spectral,
                    };
                }
                return next;
            });
        },
        [setSelectedMap]
    );

    const clearSelection = useCallback(() => setSelectedMap({}), [setSelectedMap]);

    const canViewFullCharts = pode('graficos.completos');

    useEffect(() => {
        if (status === 'active') setShowDeleted(false);
    }, [status, setShowDeleted]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const startFrom = committedFilters.startFromDate
        ? committedFilters.startFromDate.startOf('day').unix()
        : undefined;
    const startTo = committedFilters.startToDate
        ? committedFilters.startToDate.endOf('day').unix()
        : undefined;

    const query = useMemo(() => {
        return {
            id: committedFilters.experimentId?.trim() || undefined,
            status,
            page,
            size,
            dir: committedFilters.dir,
            sort: committedFilters.sort,
            serialNumber: committedFilters.serialNumber?.trim() || undefined,
            protocolId: committedFilters.protocolId?.trim() || undefined,
            analysisId: committedFilters.analysisId?.trim() || undefined,
            // '' = todos -> nao envia o parametro. Só 'true'/'false' viram booleano.
            temLabResult:
                committedFilters.temLabResult === ''
                    ? undefined
                    : committedFilters.temLabResult === 'true',
            startFrom,
            startTo,
            ativo: !showDeleted,
        };
    }, [status, page, size, committedFilters, startFrom, startTo, showDeleted]);

    const allProtocols = useMemo(
        () => bootstrap?.protocolos || [],
        [bootstrap?.protocolos]
    );

    const { groups, loading, pageInfo, refetch } = useExperimentGroups(
        query,
        allProtocols
    );

    const firmwareVersionOptions = useMemo(() => {
        if (!groups) return [];
        const versions = new Set();
        groups.forEach((dayGroup) => {
            dayGroup.protos?.forEach((proto) => {
                proto.analyses?.forEach((analysis) => {
                    analysis.items?.forEach((item) => {
                        if (item.firmwareVersion) {
                            versions.add(item.firmwareVersion);
                        }
                    });
                });
            });
        });
        return [...versions].sort((a, b) => {
            const pa = a.split('.').map(Number);
            const pb = b.split('.').map(Number);
            for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
                const diff = (pb[i] || 0) - (pa[i] || 0);
                if (diff !== 0) return diff;
            }
            return 0;
        });
    }, [groups]);

    const firmwareFilter = committedFilters.firmwareVersion?.trim() || '';

    const displayGroups = useMemo(() => {
        if (!groups) return [];

        return groups.reduce((accDayGroup, dayGroup) => {
            const validProtos = dayGroup.protos.reduce((accProto, proto) => {
                const validAnalyses = proto.analyses.reduce(
                    (accAnalysis, analysis) => {
                        const filteredItems = analysis.items.filter((item) => {
                            if (
                                showDeleted
                                    ? item.ativo !== false
                                    : item.ativo === false
                            ) {
                                return false;
                            }
                            if (
                                firmwareFilter &&
                                item.firmwareVersion !== firmwareFilter
                            ) {
                                return false;
                            }
                            return true;
                        });

                        if (filteredItems.length > 0) {
                            accAnalysis.push({
                                ...analysis,
                                items: filteredItems,
                            });
                        }
                        return accAnalysis;
                    },
                    []
                );

                if (validAnalyses.length > 0) {
                    accProto.push({ ...proto, analyses: validAnalyses });
                }
                return accProto;
            }, []);

            if (validProtos.length > 0) {
                accDayGroup.push({ ...dayGroup, protos: validProtos });
            }
            return accDayGroup;
        }, []);
    }, [groups, showDeleted, firmwareFilter]);

    useEffect(() => {
        if (!displayGroups || displayGroups.length === 0) return;

        let isMounted = true;

        const fetchMissingTowers = async () => {
            const serialsToFetch = new Set();
            displayGroups.forEach((group) => {
                group.protos.forEach((proto) => {
                    proto.analyses.forEach((analysis) => {
                        analysis.items.forEach((exp) => {
                            if (
                                exp.serialNumber &&
                                !towersData[exp.serialNumber]
                            ) {
                                serialsToFetch.add(exp.serialNumber);
                            }
                        });
                    });
                });
            });

            if (serialsToFetch.size === 0) return;

            setLoadingTowers((prev) => new Set([...prev, ...serialsToFetch]));

            const promises = Array.from(serialsToFetch).map(async (serial) => {
                try {
                    const results = await searchTowers(serial);
                    const match =
                        results.find((t) => t.numeroSerie === serial) ||
                        results[0];
                    return {
                        serial,
                        data: match
                            ? { tag: match.tag, tagColor: match.tagColor }
                            : null,
                    };
                } catch (error) {
                    return { serial, data: null };
                }
            });

            const results = await Promise.all(promises);

            if (isMounted) {
                setTowersData((prev) => {
                    const next = { ...prev };
                    let hasChanges = false;
                    results.forEach(({ serial, data }) => {
                        if (data && !next[serial]) {
                            next[serial] = data;
                            hasChanges = true;
                        }
                    });
                    return hasChanges ? next : prev;
                });
                setLoadingTowers((prev) => {
                    const next = new Set(prev);
                    results.forEach(({ serial }) => next.delete(serial));
                    return next;
                });
            }
        };

        fetchMissingTowers();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayGroups]);

    const hasDisplayItems = displayGroups.length > 0;
    const totalPages = pageInfo.totalPages || 0;

    // Se a página salva (sessionStorage) ficou além do total real — ex.: troca de
    // cliente após logout, exclusão, ou mudança de filtro — cai na última página
    // válida em vez de mostrar "nada encontrado".
    useEffect(() => {
        if (!loading && totalPages > 0 && page > totalPages - 1) {
            setPage(Math.max(0, totalPages - 1));
        }
    }, [loading, totalPages, page, setPage]);

    useEffect(() => {
        if (!loading) {
            const savedPosition = sessionStorage.getItem(
                'experimentsPage_scrollPosition'
            );
            if (savedPosition !== null) {
                window.scrollTo(0, parseInt(savedPosition, 10));
                sessionStorage.removeItem('experimentsPage_scrollPosition');
            }
        }
    }, [loading]);

    const canViewGraphs = useMemo(() => {
        if (!canViewFullCharts) return true;
        return (
            bootstrap?.menu?.itensMenus?.some(
                (item) => item.rotas === '/graficos'
            ) ?? false
        );
    }, [bootstrap, canViewFullCharts]);

    const protocolOptions = useMemo(
        () => allProtocols.map((p) => ({ id: p.id, label: p.name || p.id })),
        [allProtocols]
    );
    const selectedProtocol = useMemo(
        () => protocolOptions.find((o) => o.id === filters.protocolId) || null,
        [protocolOptions, filters.protocolId]
    );

    // Análises do protocolo selecionado (ex.: Afluente/Efluente dentro do protocolo de
    // efluente). Sem protocolo escolhido a lista fica vazia e o campo desabilitado —
    // filtrar por análise sem protocolo não faz sentido no domínio.
    const analysisOptions = useMemo(() => {
        if (!filters.protocolId) return [];
        const proto = allProtocols.find((p) => p.id === filters.protocolId);
        const analises = proto?.analises || proto?.analysis || [];
        return analises
            .map((a) => ({
                id: a?.id || a?.analiseId || '',
                label: a?.name || a?.nome || a?.id || '',
            }))
            .filter((o) => o.id);
    }, [allProtocols, filters.protocolId]);

    const selectedAnalysis = useMemo(
        () => analysisOptions.find((o) => o.id === filters.analysisId) || null,
        [analysisOptions, filters.analysisId]
    );

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (committedFilters.experimentId) count++;
        if (committedFilters.serialNumber) count++;
        if (committedFilters.protocolId) count++;
        if (committedFilters.analysisId) count++;
        if (committedFilters.temLabResult) count++;
        if (committedFilters.firmwareVersion) count++;
        if (committedFilters.startFromDate) count++;
        if (committedFilters.startToDate) count++;
        return count;
    }, [committedFilters]);

    const hasAnyFilter = useMemo(() => {
        return !!(
            filters.experimentId ||
            filters.serialNumber ||
            filters.protocolId ||
            filters.firmwareVersion ||
            filters.startFromDate ||
            filters.startToDate ||
            showDeleted
        );
    }, [filters, showDeleted]);

    const handleClearFilters = () => {
        setFilters(initialFilters);
        setCommittedFilters(initialFilters);
        setPage(0);
        setShowDeleted(false);
        setViewedExperiments(new Set());
        setLastViewedExperimentId(null);
    };

    const handleSearch = () => {
        setPage(0);
        setCommittedFilters(filters);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') handleSearch();
    };

    const handleAction = ({ type, experiment }) => {
        if (type === 'view') {
            const experimentId = String(experiment.id);
            const newViewed = new Set(viewedExperiments).add(experimentId);

            setViewedExperiments(newViewed);
            setLastViewedExperimentId(experimentId);
            sessionStorage.setItem(
                'experimentsPage_scrollPosition',
                window.scrollY
            );

            if (!canViewFullCharts) {
                setBasicModalExp(experiment);
            } else {
                navigate(`/${tenant}/graficos/${experimentId}`, {
                    state: { experiment },
                });
            }
        } else if (type === 'refresh') {
            // O card alterou o experimento (excluiu, restaurou, encerrou, comentou...).
            refetch();
        } else if (type === 'process') {
            setProcessTarget(experiment);
            setProcessOpen(true);
        }
    };

    const toggleButtonSx = {
        boxShadow: '0 2px 4px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
        '&.Mui-selected, &.Mui-selected:hover': {
            color: 'white',
            backgroundColor: 'primary.main',
            boxShadow: 'inset 0 3px 7px rgba(0,0,0,0.32), inset 0 1px 2px rgba(0,0,0,0.18)',
        },
        flexGrow: 1,
    };

    const canShowDeletedToggle = status === 'completed' && pode('lixeira.ver');

    const dayGroupProps = {
        handleAction,
        canViewGraphs,
        viewedExperiments,
        lastViewedExperimentId,
        towersData,
        loadingTowers,
        protocols: allProtocols,
        selectable: selectionMode,
        selectedIds,
        onToggleSelect: handleToggleSelect,
    };

    if (loading && groups.length === 0) {
        return (
            <Box sx={{ p: { xs: 1.5, md: 2 }, width: '100%' }}>
                <Stack spacing={2}>
                    <Skeleton variant="text" width="60%" height={40} />
                    <Skeleton variant="rectangular" height={200} />
                    <Skeleton variant="rectangular" height={200} />
                </Stack>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <Box
                sx={{
                    p: { xs: 1.5, md: 2 },
                    width: '100%',
                    overflowX: 'hidden',
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                    gap={1}
                >
                    {/* Título — encolhe para ceder espaço aos controles */}
                    <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 800,
                            letterSpacing: -0.5,
                            lineHeight: 1.2,
                            color: showDeleted ? 'error.main' : 'text.primary',
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {status === 'active'
                            ? isMobile ? 'Em Andamento' : 'Amostras em Andamento'
                            : showDeleted
                              ? isMobile ? 'Lixeira' : 'Lixeira (Excluídos)'
                              : isMobile ? 'Encerradas' : 'Amostras Encerradas'}
                    </Typography>

                    {/* Controles — nunca encolhem */}
                    <Stack direction="row" alignItems="center" spacing={0.75} flex="0 0 auto">
                        <Tooltip
                            title={
                                selectionMode
                                    ? 'Sair da seleção'
                                    : 'Selecionar experimentos para a curva de crescimento'
                            }
                        >
                            <IconButton
                                size="small"
                                onClick={() => setSelectionMode((v) => !v)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: selectionMode ? 'primary.main' : 'divider',
                                    borderRadius: 1.5,
                                    color: selectionMode ? 'primary.main' : 'text.secondary',
                                    '&:hover': {
                                        color: 'primary.main',
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                <Lucide.ChartSpline size={18} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Recarregar amostras">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={refetch}
                                    disabled={loading}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'primary.main',
                                            borderColor: 'primary.main',
                                        },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            transition: 'transform 0.4s ease',
                                            ...(loading && {
                                                animation: 'spin 1s linear infinite',
                                                '@keyframes spin': {
                                                    from: { transform: 'rotate(0deg)' },
                                                    to: { transform: 'rotate(360deg)' },
                                                },
                                            }),
                                        }}
                                    >
                                        <Lucide.RefreshCw size={18} />
                                    </Box>
                                </IconButton>
                            </span>
                        </Tooltip>

                        <ToggleButtonGroup
                            value={status}
                            exclusive
                            onChange={(_, val) => {
                                if (val) {
                                    const newSort = val === 'active' ? 'startDate' : 'endDate';
                                    const newFilters = { ...filters, sort: newSort };
                                    setStatus(val);
                                    setPage(0);
                                    setFilters(newFilters);
                                    setCommittedFilters(newFilters);
                                }
                            }}
                            size="small"
                        >
                            <ToggleButton
                                value="active"
                                sx={{
                                    ...toggleButtonSx,
                                    flexGrow: 0,
                                    whiteSpace: 'nowrap',
                                    px: { xs: 1.25, sm: 2 },
                                    fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                                    letterSpacing: 0,
                                }}
                            >
                                {isMobile ? 'Ativas' : 'Em andamento'}
                            </ToggleButton>
                            <ToggleButton
                                value="completed"
                                sx={{
                                    ...toggleButtonSx,
                                    flexGrow: 0,
                                    whiteSpace: 'nowrap',
                                    px: { xs: 1.25, sm: 2 },
                                    fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                                    letterSpacing: 0,
                                }}
                            >
                                Encerradas
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Stack>

                {selectionMode && (
                    <Paper
                        variant="outlined"
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 1.5,
                            p: 1.5,
                            mb: 2,
                            borderColor: 'primary.main',
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <Lucide.ChartSpline size={18} color={theme.palette.primary.main} />
                            <Typography variant="body2" fontWeight={700}>
                                {selectedExperiments.length} selecionado(s)
                            </Typography>
                            {selectionMixed && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    icon={<Lucide.CircleAlert size={14} />}
                                    label="Análises diferentes — réplicas deveriam ser da mesma análise"
                                    sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                                />
                            )}
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexShrink={0}>
                            <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={clearSelection}
                                disabled={selectedExperiments.length === 0}
                            >
                                Limpar
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<Lucide.ChartSpline size={16} />}
                                onClick={() => setGrowthModalOpen(true)}
                                disabled={selectedExperiments.length === 0}
                            >
                                Gerar curva
                            </Button>
                        </Stack>
                    </Paper>
                )}

                <FiltrosPanel
                    defaultExpanded={hasAnyFilter}
                    destaque={showDeleted}
                    tituloAlternativo="Filtros (Modo Excluídos)"
                    ativos={activeFilterCount}
                    loading={loading}
                    onLimpar={handleClearFilters}
                    onBuscar={handleSearch}
                >
                            {canShowDeletedToggle && (
                                <Grid item xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            mb: 1,
                                            borderColor: 'divider',
                                            bgcolor: 'background.default',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={showDeleted}
                                                    onChange={(e) => {
                                                        setShowDeleted(
                                                            e.target.checked
                                                        );
                                                        setPage(0);
                                                    }}
                                                    size="small"
                                                    color="error"
                                                />
                                            }
                                            label={
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    <Lucide.Trash2
                                                        color={theme.palette.error.main}
                                                        size={18}
                                                    />
                                                    <Typography variant="body2">
                                                        Visualizar experimentos
                                                        excluídos (Lixeira)
                                                    </Typography>
                                                </Stack>
                                            }
                                            sx={{ ml: 0.5 }}
                                        />
                                    </Paper>
                                </Grid>
                            )}

                            <Grid item {...CAMPO}>
                                <TextField
                                    label="ID do experimento"
                                    value={filters.experimentId}
                                    onChange={(e) =>
                                        handleFilterChange(
                                            'experimentId',
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={handleKeyDown}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lucide.Search
                                                    size={18}
                                                    color={theme.palette.action.active}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item {...CAMPO}>
                                <TextField
                                    label="Número de série"
                                    value={filters.serialNumber}
                                    onChange={(e) =>
                                        handleFilterChange(
                                            'serialNumber',
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={handleKeyDown}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lucide.Hash
                                                    size={18}
                                                    color={theme.palette.action.active}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item {...CAMPO}>
                                <Autocomplete
                                    options={protocolOptions}
                                    value={selectedProtocol}
                                    onChange={(_, v) => {
                                        // Zera a análise junto: ela pertence ao protocolo
                                        // anterior e ficaria órfã no filtro.
                                        setFilters((prev) => ({
                                            ...prev,
                                            protocolId: v?.id || '',
                                            analysisId: '',
                                        }));
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Protocolo"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lucide.FlaskConical
                                                            size={18}
                                                            color={theme.palette.action.active}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(a, b) =>
                                        a?.id === b?.id
                                    }
                                />
                            </Grid>
                            <Grid item {...CAMPO}>
                                <Autocomplete
                                    options={analysisOptions}
                                    value={selectedAnalysis}
                                    disabled={!filters.protocolId}
                                    onChange={(_, v) =>
                                        handleFilterChange(
                                            'analysisId',
                                            v?.id || ''
                                        )
                                    }
                                    noOptionsText="Nenhuma análise neste protocolo"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Análise"
                                            size="small"
                                            placeholder={
                                                filters.protocolId
                                                    ? 'Todas'
                                                    : 'Selecione o protocolo'
                                            }
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lucide.Microscope
                                                            size={18}
                                                            color={theme.palette.action.active}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(a, b) =>
                                        a?.id === b?.id
                                    }
                                />
                            </Grid>
                            <Grid item {...CAMPO}>
                                <Autocomplete
                                    options={firmwareVersionOptions}
                                    value={filters.firmwareVersion || null}
                                    onChange={(e, newValue) =>
                                        handleFilterChange(
                                            'firmwareVersion',
                                            newValue || ''
                                        )
                                    }
                                    size="small"
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Versão do firmware"
                                            placeholder="Selecione a versão"
                                            onKeyDown={handleKeyDown}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <Lucide.MemoryStick
                                                                size={18}
                                                                color={theme.palette.action.active}
                                                            />
                                                        </InputAdornment>
                                                        {
                                                            params.InputProps
                                                                .startAdornment
                                                        }
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Resultado de laboratorio: o lancamento de bancada que
                                serve de referencia para o modelo. O laboratorista as
                                vezes quer processar SO o que ja tem esse lancamento —
                                ou achar o que ainda falta lancar. O cruzamento e feito
                                no backend porque lab_results e outra colecao: filtrar
                                aqui so alcancaria a pagina atual e o total sairia
                                errado. */}
                            <Grid item {...CAMPO}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Resultado de laboratório</InputLabel>
                                    <Select
                                        label="Resultado de laboratório"
                                        value={filters.temLabResult}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'temLabResult',
                                                e.target.value
                                            )
                                        }
                                        startAdornment={
                                            <InputAdornment
                                                position="start"
                                                sx={{ mr: 1 }}
                                            >
                                                <Lucide.FlaskConical
                                                    size={18}
                                                    color={theme.palette.action.active}
                                                />
                                            </InputAdornment>
                                        }
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        <MenuItem value="true">
                                            Com resultado de lab.
                                        </MenuItem>
                                        <MenuItem value="false">
                                            Sem resultado de lab.
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item {...CAMPO}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Ordenar por</InputLabel>
                                    <Select
                                        label="Ordenar por"
                                        value={filters.sort}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'sort',
                                                e.target.value
                                            )
                                        }
                                        startAdornment={
                                            <InputAdornment
                                                position="start"
                                                sx={{ mr: 1 }}
                                            >
                                                <Lucide.ArrowDownUp
                                                    size={18}
                                                    color={theme.palette.action.active}
                                                />
                                            </InputAdornment>
                                        }
                                    >
                                        {SORT_OPTIONS.map((o) => (
                                            <MenuItem
                                                key={o.value}
                                                value={o.value}
                                            >
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={3} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Direção</InputLabel>
                                    <Select
                                        label="Direção"
                                        value={filters.dir}
                                        onChange={(e) =>
                                            handleFilterChange(
                                                'dir',
                                                e.target.value
                                            )
                                        }
                                        startAdornment={
                                            <InputAdornment
                                                position="start"
                                                sx={{ mr: 1 }}
                                            >
                                                <Lucide.ArrowUpDown
                                                    size={18}
                                                    color={theme.palette.action.active}
                                                />
                                            </InputAdornment>
                                        }
                                    >
                                        <MenuItem value="desc">Desc</MenuItem>
                                        <MenuItem value="asc">Asc</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            <Grid item xs={6} md={3}>
                                <DatePicker
                                    label="Início de"
                                    value={filters.startFromDate}
                                    onChange={(newValue) =>
                                        handleFilterChange(
                                            'startFromDate',
                                            newValue
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lucide.Calendar
                                                            size={18}
                                                            color={theme.palette.action.active}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <DatePicker
                                    label="Início até"
                                    value={filters.startToDate}
                                    onChange={(newValue) =>
                                        handleFilterChange(
                                            'startToDate',
                                            newValue
                                        )
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lucide.Calendar
                                                            size={18}
                                                            color={theme.palette.action.active}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                </FiltrosPanel>

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {!loading && !hasDisplayItems && (
                    <Stack
                        sx={{
                            textAlign: 'center',
                            p: { xs: 3, sm: 5 },
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '30vh',
                            mx: { xs: 1, sm: 0 },
                        }}
                        spacing={2}
                    >
                        <Lucide.SearchX
                            size={64}
                            color={theme.palette.text.secondary}
                        />
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {showDeleted
                                ? 'Nenhum experimento excluído encontrado'
                                : 'Nenhuma amostra encontrada'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {showDeleted
                                ? 'A lixeira está vazia.'
                                : 'Tente ajustar seus filtros.'}
                        </Typography>
                    </Stack>
                )}

                {!loading && hasDisplayItems && (
                    <>
                        <Box>
                            <Timeline
                                sx={{
                                    p: 0,
                                    width: '100%',
                                    '::before': { display: 'none' },
                                }}
                            >
                                {displayGroups.map(
                                    ({ date, protos }, index) => (
                                        <TimelineItem
                                            key={`section-${date}-${index}`}
                                            sx={{
                                                '::before': { display: 'none' },
                                                minWidth: 0,
                                                width: '100%',
                                            }}
                                        >
                                            <TimelineSeparator
                                                sx={{
                                                    display: {
                                                        xs: 'none',
                                                        sm: 'flex',
                                                    },
                                                }}
                                            >
                                                <TimelineDot
                                                    color={
                                                        showDeleted
                                                            ? 'error'
                                                            : 'primary'
                                                    }
                                                >
                                                    <Lucide.CalendarDays size={18} />
                                                </TimelineDot>
                                                {index <
                                                    displayGroups.length -
                                                        1 && (
                                                    <TimelineConnector />
                                                )}
                                            </TimelineSeparator>
                                            <TimelineContent
                                                sx={{
                                                    py: 1.5,
                                                    px: { xs: 0, sm: 2 },
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <DayGroupContent
                                                    date={date}
                                                    protos={protos}
                                                    {...dayGroupProps}
                                                    isMobile={isMobile}
                                                />
                                            </TimelineContent>
                                        </TimelineItem>
                                    )
                                )}
                            </Timeline>
                        </Box>

                        {/* `page` aqui e 0-based (vem do Spring); o rodape e 1-based. */}
                        <PaginacaoFooter
                            page={page + 1}
                            totalPages={totalPages}
                            onPageChange={(v) => setPage(v - 1)}
                            itemsPerPage={size}
                            onItemsPerPageChange={(n) => {
                                setSize(n);
                                setPage(0);
                            }}
                            itemsPerPageOptions={[10, 20, 50, 100]}
                            loading={loading}
                        />
                    </>
                )}

                <ProcessDialog
                    open={processOpen}
                    onClose={() => setProcessOpen(false)}
                    experiment={processTarget}
                    onProcessado={refetch}
                />

                <BasicChartModal
                    experiment={basicModalExp}
                    onClose={() => setBasicModalExp(null)}
                />

                <GrowthCurveModal
                    open={growthModalOpen}
                    experiments={selectedExperiments}
                    onClose={() => setGrowthModalOpen(false)}
                />
            </Box>
        </LocalizationProvider>
    );
}
